import { useEffect, useState } from "react";
import ApiMethods from "../../ApiManager/ApiMethods";

/*
  Shared, module-level member cache used by both the Members screen and the
  New Appointment / Edit Appointment member picker (NewAppointmentFlow.jsx),
  so the two stop independently re-fetching the same client list + running
  their own N+1 "one client-packages request per client" loop.

  Before this existed, loading the Members screen alone fired ~250+
  sequential `client-packages?client_id=X` requests (one per member) and
  took several seconds; opening New Appointment moments later repeated the
  exact same fetch from scratch. This module fetches once, caches it for
  STALE_TIME_MS, and shares it (plus mutation helpers so add/edit/delete
  update the cache directly instead of forcing a full reload) across every
  consumer via a tiny subscriber pattern — no React Query/Redux is
  installed in this app, so this is the smallest thing that gives the same
  behavior (instant cached reads, background refresh, single in-flight
  request dedup).
*/

const STALE_TIME_MS = 60 * 1000;

let state = {
  clients: [],
  packagesByClient: {},
  loading: false,
  error: false,
  lastFetchedAt: 0,
};

let inFlightPromise = null;
const listeners = new Set();

// Guards against a real race: a `doFetch()` already in flight (e.g. kicked off the moment New
// Appointment's member picker mounted) can resolve *after* `upsertClientInCache` adds a
// just-created client — its response reflects the DB as it was when the request was sent
// (before the insert), so a plain wholesale overwrite in `doFetch()` would silently discard the
// brand-new client from the cache until the next 60s-staleness refresh happened to catch it.
// Tracks every client id newly *inserted* (not merged) via `upsertClientInCache`, keyed to the
// object itself, so `doFetch()` can re-add any of these missing from its own response. Cleared
// per id as soon as some fetch's response does contain it for real (see doFetch below) — this is
// a short-lived patch over one unlucky race, not a permanent parallel source of truth.
const pendingLocalAdditions = new Map();

function notify() {
  listeners.forEach((cb) => cb(state));
}

function setState(patch) {
  state = { ...state, ...patch };
  notify();
}

// One query for every requested client's packages instead of one request
// per client. Falls back to the old per-client loop ONLY when the batch
// route itself doesn't exist (404 — an older deployed backend that predates
// this endpoint). Any other failure (401, 500, network error, ...) applies
// identically to every one of the per-client requests too, so retrying
// N times in that case would just fire N more failing requests for nothing
// — this used to happen on every load whenever the whole `client-packages`
// family was unreachable (e.g. undeployed/unauthorized), turning a single
// failed batch call into 250+ doomed individual ones.
async function fetchPackagesForClients(ids) {
  if (ids.length === 0) return {};
  try {
    const rows =
      (await ApiMethods.get(`client-packages/batch?client_ids=${ids.join(",")}`)) ||
      [];
    const grouped = {};
    rows.forEach((row) => {
      if (!grouped[row.client_id]) grouped[row.client_id] = [];
      grouped[row.client_id].push(row);
    });
    return grouped;
  } catch (err) {
    if (err?.statusCode !== 404) {
      return Object.fromEntries(ids.map((id) => [id, []]));
    }
    const entries = await Promise.all(
      ids.map(async (id) => {
        try {
          const pkgs = await ApiMethods.get(`client-packages?client_id=${id}`);
          return [id, pkgs || []];
        } catch (e) {
          return [id, []];
        }
      })
    );
    return Object.fromEntries(entries);
  }
}

async function doFetch() {
  setState({ loading: true, error: false });
  try {
    const clients = (await ApiMethods.get("client")) || [];
    // NOT reconciled against pendingLocalAdditions yet — deliberately deferred past the second
    // await just below (see the comment down there for why: reconciling here would still lose an
    // insert that lands during that second await's own window).
    const packagesByClient = await fetchPackagesForClients(
      clients.map((c) => c.id)
    );
    // Reconcile against pendingLocalAdditions (see its declaration above) right before the
    // commit, not right after the `clients` GET above — this whole function still has another
    // `await` (fetchPackagesForClients, just above) between that GET resolving and this
    // `setState` actually landing, and `upsertClientInCache` can run at any point during either
    // await. Checking immediately after the GET would still miss an insert that happens to land
    // in that second window: it wouldn't be in `pendingLocalAdditions` yet at check time, then
    // this function's own (now-stale) `clients` would still steamroll it. Reading
    // pendingLocalAdditions this late instead — its freshest state, right before it's used —
    // closes that gap. A client actually IS in `clients` when a fetch that started after its
    // creation finally caught up, so it's safe to stop special-casing it; one that's still
    // missing must have raced this whole fetch, so it's kept.
    const ids = new Set(clients.map((c) => c.id));
    pendingLocalAdditions.forEach((client, id) => {
      if (ids.has(id)) {
        pendingLocalAdditions.delete(id);
      } else {
        clients.unshift(client);
      }
    });
    setState({
      clients,
      packagesByClient,
      loading: false,
      error: false,
      lastFetchedAt: Date.now(),
    });
  } catch (err) {
    console.error("Error fetching members:", err);
    setState({ loading: false, error: true });
  }
}

// Serves cached data immediately when it's fresh; otherwise fetches once
// and shares that single in-flight request with every concurrent caller
// (so two components mounting at the same moment — or React StrictMode's
// double-invoke in dev — don't double the request count).
export function ensureMembersLoaded({ force = false } = {}) {
  const isStale = Date.now() - state.lastFetchedAt > STALE_TIME_MS;
  if (!force && state.clients.length > 0 && !isStale) {
    return Promise.resolve(state);
  }
  if (inFlightPromise) return inFlightPromise;
  inFlightPromise = doFetch().finally(() => {
    inFlightPromise = null;
  });
  return inFlightPromise;
}

export function refreshMembers() {
  return ensureMembersLoaded({ force: true });
}

// Marks the cache stale without clearing what's already shown — the next
// `ensureMembersLoaded()` call (from any screen) will refetch silently in
// the background instead of showing a loading state over stale-but-still-
// displayed data.
export function invalidateMembersCache() {
  state = { ...state, lastFetchedAt: 0 };
}

export function getMembersState() {
  return state;
}

export function subscribeMembers(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Add (if new) or merge (if existing) a single client into the cache —
// used after create/edit so callers never need a full list refetch just to
// reflect one row's change.
export function upsertClientInCache(client) {
  if (!client || client.id == null) return;
  const idx = state.clients.findIndex((c) => c.id === client.id);
  const clients =
    idx === -1
      ? [client, ...state.clients]
      : state.clients.map((c) => (c.id === client.id ? { ...c, ...client } : c));
  // Only a genuine new insert needs the doFetch() race guard above — an edit to an already-known
  // client isn't at risk of being wholesale-dropped by a stale in-flight fetch, since that fetch
  // would just bring back its (at worst slightly outdated) pre-edit row, not erase it entirely.
  if (idx === -1) pendingLocalAdditions.set(client.id, client);
  setState({ clients });
}

export function removeClientFromCache(clientId) {
  pendingLocalAdditions.delete(clientId);
  setState({ clients: state.clients.filter((c) => c.id !== clientId) });
}

export function setClientPackagesInCache(clientId, packages) {
  setState({
    packagesByClient: { ...state.packagesByClient, [clientId]: packages },
  });
}

// Adds (or replaces, by id) a single package cycle for a client without
// needing the caller to already hold that client's full package list —
// used right after assign/edit so the Members screen and package details
// popup update immediately, with no full refetch.
export function upsertClientPackageInCache(clientId, clientPackage) {
  if (!clientPackage || clientPackage.id == null) return;
  const existing = state.packagesByClient[clientId] || [];
  const idx = existing.findIndex((p) => p.id === clientPackage.id);
  const next =
    idx === -1
      ? [clientPackage, ...existing]
      : existing.map((p) => (p.id === clientPackage.id ? clientPackage : p));
  setState({
    packagesByClient: { ...state.packagesByClient, [clientId]: next },
  });
}

// Re-fetches just one client's package/booking-usage rows and merges them into the cache —
// used after an appointment is created/edited/cancelled, since the backend computes "used"
// live from `cal_bookings` (no usage counter to read off the mutation's own response) and a
// full `doFetch()`/`refreshMembers()` would otherwise re-fetch every client just to reflect
// one person's change. Best-effort: a failed background refresh here shouldn't surface as a
// user-facing error (the booking itself already succeeded) — the next natural full load still
// picks it up.
export async function refreshClientPackages(clientId) {
  if (clientId == null) return;
  try {
    const packages = await ApiMethods.get(`client-packages?client_id=${clientId}`);
    setClientPackagesInCache(clientId, packages || []);
  } catch (err) {
    console.error("Error refreshing client packages:", err);
  }
}

// Subscribes to the shared cache and kicks off a load (cached/instant if
// fresh, silent background refresh if stale, real fetch if empty).
export function useMembers() {
  const [snapshot, setSnapshot] = useState(getMembersState);

  useEffect(() => {
    const unsubscribe = subscribeMembers(setSnapshot);
    ensureMembersLoaded();
    return unsubscribe;
  }, []);

  return snapshot;
}
